import React from "react";
import { motion } from "framer-motion";
import { Leaf, Sparkles, Droplets, HeartPulse, Wind, ShieldCheck, Check, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLang } from "@/context/LanguageContext";
import { IMG_NAGAYU } from "@/i18n/translations";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

const ICONS = [Sparkles, ShieldCheck, Leaf, HeartPulse, Wind, Droplets];

export const NagayuSection = () => {
  const { t } = useLang();
  const n = t.nagayu;

  return (
    <section id="nagayu" className="relative py-24 sm:py-32 bg-brand text-brand-cream overflow-hidden" data-testid="nagayu">
      <div className="grain absolute inset-0 opacity-40" />
      {/* CO2 ripple bubbles */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        {[
          { l: "12%", b: "8%", s: 60, d: "0s" },
          { l: "78%", b: "20%", s: 90, d: "0.8s" },
          { l: "45%", b: "5%", s: 40, d: "1.4s" },
          { l: "88%", b: "60%", s: 50, d: "0.4s" },
        ].map((b, i) => (
          <span key={i} className="absolute rounded-full border border-bamboo/40 animate-ripple" style={{ left: b.l, bottom: b.b, width: b.s, height: b.s, animationDelay: b.d }} />
        ))}
      </div>

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <span className="inline-flex items-center gap-2 rounded-full bg-bamboo/20 border border-bamboo/40 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.15em] text-bamboo-soft">
            <Leaf className="h-3.5 w-3.5" /> {n.tag}
          </span>
          <h2 className="mt-5 font-heading text-5xl sm:text-6xl font-medium tracking-tight leading-none">{n.title}</h2>
          <p className="mt-5 text-brand-cream/80 leading-relaxed text-lg max-w-xl">{n.subtitle}</p>

          <div className="mt-8 rounded-2xl glass-dark border border-white/15 p-6">
            <p className="font-heading text-2xl font-semibold italic text-bamboo-soft">{n.how_title}</p>
            <p className="mt-2 text-brand-cream/80 leading-relaxed">{n.how_text}</p>
          </div>

          <Button
            data-testid="nagayu-cta"
            onClick={() => scrollTo("grooming")}
            className="mt-8 rounded-full bg-bamboo hover:bg-bamboo-hover text-brand-cream font-bold h-13 px-8 py-6 text-base no-a11y-underline group"
          >
            {n.cta}
            <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform duration-200" />
          </Button>
          <p className="mt-4 text-xs text-brand-cream/50 max-w-md">{n.note}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <div className="relative rounded-[2rem] overflow-hidden border-4 border-white/15 shadow-2xl mb-6">
            <img src={IMG_NAGAYU} alt="Σκύλος στο Care For Me" className="w-full h-[240px] object-cover object-top" />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {n.benefits.map((b, i) => {
              const Icon = ICONS[i];
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}
                  className="rounded-2xl bg-white/8 border border-white/12 p-4 hover:bg-white/12 transition-colors duration-300"
                  data-testid={`nagayu-benefit-${i}`}
                >
                  <span className="grid place-items-center h-9 w-9 rounded-full bg-bamboo/25 text-bamboo-soft mb-2.5">
                    <Icon className="h-4.5 w-4.5" />
                  </span>
                  <h3 className="font-bold text-sm text-brand-cream flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-bamboo-soft" /> {b.t}</h3>
                  <p className="mt-1 text-xs text-brand-cream/65 leading-relaxed">{b.d}</p>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </div>
    </section>
  );
};
